import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import tinycolor from 'tinycolor2';
import { useLocation } from 'react-router';

import Button from '../../shared/_TBComponents/Button';
import HeaderText from '../../shared/_TBComponents/HeaderText';
import Spinner from '../../shared/_TBComponents/Spinner';
import LightLogo from '../../shared/images/poweredByTBWhite.png';
import DarkLogo from '../../shared/images/poweredByTBBlack.png';
import ViewOnlyVTChartWrapper from './components/presentational/ViewOnlyVTChartWrapper';
import SharedTemplateAboutModal from '../sports-science/components/presentational/sharedTemplateComponents/SharedTemplateAboutModal';
import {
  fetchPublicVTDashData,
  setVTDashView,
  setAboutVTDashModal,
} from './ducks/volumeTrackerDashboardActions';
import InvalidTokenModal from '../sport-coach-dashboard/components/presentational/InvalidTokenModal';

const Container = styled('div')`
  width: 100%;
`;

const Wrapper = styled('div')`
  height: 100%;
`;

const PageWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #F5F5F5;
`;

const DashboardBanner = styled('div')`
  background-color: ${(props) => (props.bgColor)};
  height: 100px;
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #FFFFFF;
`;

const UserLogo = styled('img')`
  height: 75px;
`;

const TBuildrLogo = styled('div')`
  background-image: ${(props) => (tinycolor(props.bgColor).isLight() ? `url(${DarkLogo})` : `url(${LightLogo})`)};
  background-repeat: no-repeat;
  height: 46px;
  width: 200px;
`;

const DashboardHeader = styled('div')`
  display: flex;
  width: 100%;
  background-color: #FFFFFF;
  padding-top: 30px;
  padding-left: 20px;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
`;

const TitleAndInfo = styled('div')`
  display: flex;

  button {
    margin-left: 5px;
  }

  svg {
    border: 1px solid #808080;
    border-radius: 50%;
    height: 20px;
    width: 20px;
    padding: 3px;
  }
`;

const MenuContainer = styled('div')`
  display: flex;
`;

const MenuItem = styled('button')`
  border: none;
  margin-left: 10px;
  margin-right: 12px;
  font-family: 'Nunito Sans';
  font-weight: 400;
  font-size: 16px;
  border-bottom: ${(props) => (props.isActive ? '3px solid #1787FB!important' : '3px solid white')};
  color: ${(props) => (!props.isActive ? '#9E9E9E' : '#444444')};
`;

const ViewOnlyVTDash = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token');

  const dashBGColor = useSelector((state) => state.volumeTrackerDashboard.data.dashBGColor);
  const dashLogo = useSelector((state) => state.volumeTrackerDashboard.data.dashLogo);
  const dashTitle = useSelector((state) => state.volumeTrackerDashboard.data.dashTitle);
  const vtDashData = useSelector((state) => state.volumeTrackerDashboard.data.vtDashData);
  const vtDashError = useSelector((state) => state.volumeTrackerDashboard.data.vtDashError);

  const aboutModalShowing = useSelector(
    (state) => state.volumeTrackerDashboard.ui.aboutModalShowing,
  );
  const vtDashView = useSelector((state) => state.volumeTrackerDashboard.ui.vtDashView);

  useEffect(() => {
    dispatch(fetchPublicVTDashData(token));
  }, []);

  const handleMenuClick = (view) => {
    if (view !== vtDashView) {
      dispatch(setVTDashView(view));
    }
  };

  return (
    <Container>
      <Helmet>
        <title>Volume Tracker | TeamBuildr</title>
      </Helmet>
      {!vtDashData?.length && !vtDashError && (
        <Wrapper>
          <Spinner />
        </Wrapper>
      )}
      {vtDashError && (
        <InvalidTokenModal noData />
      )}
      {!!vtDashData?.length && (
        <PageWrapper>
          <DashboardBanner bgColor={dashBGColor}>
            <UserLogo src={dashLogo} alt='user logo' id='userLogo' />
            <TBuildrLogo bgColor={dashBGColor} />
          </DashboardBanner>
          <DashboardHeader>
            <TitleAndInfo>
              <HeaderText
                fontWeight='300'
                letterSpacing='normal'
              >
                {dashTitle}
              </HeaderText>
              <Button
                iconOnly
                icon='info'
                customColor='#00000000'
                iconColor='#979797'
                noBorder
                onClick={() => dispatch(setAboutVTDashModal(true))}
                rounded
              />
            </TitleAndInfo>
            <MenuContainer>
              <MenuItem
                isActive={vtDashView === 'overview'}
                onClick={() => handleMenuClick('overview')}
              >
                Overview
              </MenuItem>
              <MenuItem
                isActive={vtDashView === 'assigned'}
                onClick={() => handleMenuClick('assigned')}
              >
                Assigned/Completed
              </MenuItem>
            </MenuContainer>
          </DashboardHeader>
          {vtDashView === 'overview'
            && <ViewOnlyVTChartWrapper chartType='overview' />}
          {vtDashView === 'assigned'
            && <ViewOnlyVTChartWrapper chartType='assigned' />}
          {aboutModalShowing && (
            <SharedTemplateAboutModal dashboard='Volume Tracker' />
          )}
        </PageWrapper>
      )}
    </Container>
  );
};

export default ViewOnlyVTDash;
